import {
    addCallContextParameter,
    checkReservationExists,
    checkWorker,
    getConferenceRecordingConfiguration,
    getReservationsByTask,
    isCallTask,
    TERMINAL_RESERVATION_EVENTS
} from "../../ActionUtils";
import type { Reservation } from "~/packages/taskrouter";
import { ConferenceOptions } from "~/packages/taskrouter";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { getLogger, LoggerName } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { ContextManager, ContextInstance } from "~/modules/contextManager/ContextManager";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { CbmImpl } from "~/modules/cbm/CbmImpl";
import { Action, TaskReservation } from "~/modules/actions/Action";
import { SessionImpl } from "~/modules/session/Session/SessionImpl";
import { getConfigRegion } from "~/utils/regionUtil";
import { ClientOptionsStore } from "~/modules/client/ClientOptions/ClientOptionsStore";
import { AccountConfigDataContainer } from "~/modules/config/AccountConfig/AccountConfigImpl/AccountConfigDataContainer/AccountConfigDataContainer";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";




export interface AcceptTaskOptions {
    conferenceOptions?: ConferenceOptions;
}



































export class AcceptTask implements Action<Promise<TaskReservation>> {
    


    readonly #taskSid: string;

    


    readonly #options?: AcceptTaskOptions;

    


    readonly #defaultConferenceOptions: ConferenceOptions = {
        endConferenceOnCustomerExit: true,
        endConferenceOnExit: true
    };

    constructor(taskSid: string, options?: AcceptTaskOptions) {
        this.#taskSid = taskSid;
        this.#options = {
            ...options,
            conferenceOptions: {
                ...this.#defaultConferenceOptions,
                ...options?.conferenceOptions
            }
        };
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();

        const session = context.retrieveInstanceOf(ContextInstance.SessionImpl, SessionImpl);
        const flexSessionIdentifier = session.flexSessionIdentifier;
        const logger = getLogger(context)(LoggerName.Actions);
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        const accountConfig = context.retrieveInstanceOf(
            ContextInstance.AccountConfigDataContainer,
            AccountConfigDataContainer
        );
        const clientOptions = context.retrieveInstanceOf(ContextInstance.ClientOptionsStore, ClientOptionsStore);
        await cbm.init();

        logger.debug(`acceptTask invoked with taskSid: ${this.#taskSid}`);

        const worker = taskRouter.worker;

        


        const conferenceOptions = this.#options?.conferenceOptions ?? {};

        checkWorker(worker, {
            actionName: "AcceptTask",
            moduleName: "actions/Task",
            analytics,
            context,
            worker,
            logger,
            event: EVENTS.AcceptTaskCompleted,
            extra: {
                taskSid: this.#taskSid
            }
        });

        const reservations = getReservationsByTask(worker, this.#taskSid);
        const reservation = reservations.find((res) => res.status === "pending");

        checkReservationExists(reservation, this.#taskSid, {
            actionName: "AcceptTask",
            moduleName: "actions/Task",
            analytics,
            context,
            worker,
            logger,
            event: EVENTS.AcceptTaskCompleted,
            extra: {
                taskSid: this.#taskSid
            }
        });

        const validReservation = reservation!;

        if (!isCallTask(validReservation.task)) {
            try {
                const acceptedReservation = await cbm.acceptReservation(validReservation);
                sendWorkerTrackingEvent(EVENTS.AcceptTaskCompleted, analytics, worker, {
                    taskSid: this.#taskSid,
                    reservationSid: validReservation.sid
                });
                return { task: acceptedReservation?.task, reservation: acceptedReservation };
            } catch (error) {
                const errorMsg = `AcceptTask: Failed to accept reservation ${validReservation.sid}`;
                const errorMessage = toCauseMessage(error);
                logger.error(errorMsg, error);
                sendWorkerTrackingEvent(EVENTS.AcceptTaskCompleted, analytics, worker, {
                    error: `${errorMsg} (${errorMessage})`,
                    taskSid: this.#taskSid,
                    reservationSid: validReservation.sid
                });
                return Promise.reject(
                    createFlexSdkError(context)(
                        ErrorCode.FailedToAcceptReservation,
                        { severity: ErrorSeverity.Error },
                        errorMsg,
                        error
                    )
                );
            }
        }
        try {
            const to = addCallContextParameter(
                `flex_session_identifier=${flexSessionIdentifier}&flex_task_sid=${this.#taskSid}`,
                taskRouter.worker.attributes.contact_uri as string,
                this.#options?.conferenceOptions?.to
            );

            const effectiveConferenceOptions = {
                ...conferenceOptions,
                to,
                ...getConferenceRecordingConfiguration(
                    accountConfig.get(),
                    getConfigRegion(clientOptions),
                    validReservation.task.sid,
                    conferenceOptions
                )
            };

            let onAccepted: () => void;
            let onTerminal: (res: Reservation) => void;
            const cleanup = () => {
                TERMINAL_RESERVATION_EVENTS.forEach((event) => validReservation.removeListener(event, onTerminal));
                validReservation.removeListener("accepted", onAccepted);
            };
            
            const acceptedPromise = new Promise<void>((resolve, reject) => {
                onAccepted = () => {
                    cleanup();
                    resolve();
                };
                onTerminal = (res: Reservation) => {
                    cleanup();
                    reject(new Error(`Reservation ended with status: ${res?.status ?? "unknown"}`));
                };
                validReservation.once("accepted", onAccepted);
                TERMINAL_RESERVATION_EVENTS.forEach((event) => validReservation.once(event, onTerminal));
            });

            try {
                await Promise.all([validReservation.conference(effectiveConferenceOptions), acceptedPromise]);
            } catch (e) {
                cleanup();
                throw e;
            }

            sendWorkerTrackingEvent(EVENTS.AcceptTaskCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                reservationSid: validReservation.sid
            });

            return { task: validReservation.task, reservation: validReservation };
        } catch (error) {
            const errorMsg = `AcceptTask: Failed to create conference for reservation ${validReservation.sid}`;
            const errorMessage = toCauseMessage(error);
            logger.error(errorMsg, error);
            sendWorkerTrackingEvent(EVENTS.AcceptTaskCompleted, analytics, worker, {
                error: `${errorMsg} (${errorMessage})`,
                taskSid: this.#taskSid,
                reservationSid: validReservation.sid
            });
            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.FailedToCreateConference,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }
    }
}
